import { useTranslation } from 'next-i18next';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { V1A_NAMESPACE } from '../constants';

interface TrialDialogProps {
  open: boolean;
  onClose: () => void;
  /** Mounted only while the dialog is open (its own captcha instance, fresh fields). */
  children: ReactNode;
}

/**
 * The design's `<dialog class="np-trial-dialog">`: every "Testar grátis" CTA opens the sign-up form in it.
 * Native modal dialog (top layer, Esc, focus containment); closes on the × button, on Esc and on a click
 * on the backdrop. The design moved the hero form's DOM node into the dialog; here the dialog renders its
 * own form instance instead, so the hero card never loses its place.
 */
export function TrialDialog({ open, onClose, children }: TrialDialogProps) {
  const { t } = useTranslation(V1A_NAMESPACE);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (open && !dialog.open) {
      previousFocusRef.current = document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      const focusTimer = window.setTimeout(() => {
        dialog.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true });
      }, 50);
      return () => window.clearTimeout(focusTimer);
    }

    if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      const previousFocus = previousFocusRef.current;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    }
    return undefined;
  }, [open]);

  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose();
  };

  return (
    // The backdrop click is a pointer shortcut only: Esc (native) and the × button are the keyboard paths.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog ref={dialogRef} className='np-trial-dialog' aria-label={t('dialog.label')} onClose={onClose} onClick={handleBackdropClick}>
      <div className='np-trial-dialog__panel'>
        <button className='np-trial-dialog__close' type='button' aria-label={t('dialog.close')} onClick={onClose}>
          ×
        </button>
        <div className='np-trial-dialog__body'>{open ? children : null}</div>
      </div>
    </dialog>
  );
}
